import { css, html } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import { Layout, events } from '@omegagrid/core';
import type { DashboardEvent } from '@omegagrid/dashboard';
import { TreeDropdown, TreeEvent, TreeSource } from '@omegagrid/tree';
import { msg } from '@omegagrid/localize';
import type { DashboardViewOptions, MultiContextData, ReportContext } from '@reporting/rp-types/analytics';
import { BaseVisualizationEditor, type DashboardVisualization } from '../..';
import { DashboardItemStyle } from '../dashboardAdapter';
import { getVisualizationConfig } from '../factories';
import constants from "../../../constants";
import { UidMultiContextDataItem, cloneContext, createDashboardItemComponentId } from "../../../common";

const BORDER_STYLES = ['solid', 'dashed', 'dotted', 'double'];

@customElement('rp-visual-dashboard-editor')
export class DashboardVisualizationEditor extends BaseVisualizationEditor<DashboardVisualization> implements Layout {

	static styles = [...BaseVisualizationEditor.styles, css`
		.container-subeditor {
			height: 100%;
			overflow: hidden;
		}

		.container-subeditor > * {
			height: 100%;
		}
	`];

	@query('og-tree-dropdown.item-select')
	itemSelect: TreeDropdown;

	@queryAll('.field')
	fields: NodeListOf<HTMLElement>;

	@property({type: Number})
	itemIndex = -1;

	@property({type: String})
	activeTab: 'dashboard'|'visualization' = 'dashboard';

	/** live context data of the dashboard (the visualization instance is the source of truth) */
	get contextData() {
		return (this.visualization?.contextData ?? this.reportContext?.context) as MultiContextData;
	}

	get item(): UidMultiContextDataItem {
		if (this.itemIndex < 0) return null;
		return this.contextData?.items?.[this.itemIndex];
	}

	get itemStyle() {
		// common properties edit the dashboard-level item style (inherited by items)
		if (this.itemIndex < 0) {
			return (this.contextData?.view_options?.item_style ?? {}) as DashboardItemStyle;
		}
		return this.item?.style as DashboardItemStyle || {};
	}

	get itemId() {
		if (this.itemIndex < 0 || !this.reportContext || !this.item) return null;
		return createDashboardItemComponentId(this.reportContext.id_context, this.item);
	}

	get itemVisualization() {
		return this.visualization?.items?.get(this.itemId)?.visualization;
	}

	/**
	 * Subeditor provided by the selected item's visualization
	 * (VisualizationConfig.editorFactory), e.g. the text editor.
	 * Only items with embedded context data can be edited here,
	 * linked items are edited in their own view.
	 * Cached per item uid, so it survives structural changes
	 * (add/remove/duplicate) of the other items.
	 *
	 * The subeditor works on its own copy of the item context (subeditors
	 * mutate their reportContext directly) and every edit is committed
	 * into the store by _commitSubEditor.
	 */
	private _subEditors = new Map<string, BaseVisualizationEditor>();
	/** last committed working copy per item uid, to commit only actual subeditor changes */
	private _subEditorCommits = new Map<string, string>();
	/** item context snapshot the working copy was cloned from (external change detection) */
	private _subEditorSources = new Map<string, unknown>();
	get subEditor() {
		const item = this.item;
		if (!item?.context) return null;

		const config = getVisualizationConfig(item.context);
		if (!config?.editorFactory) return null;

		const key = item.uid ?? this.itemIndex.toString();

		// the item context changed outside the subeditor (sidebar filter,
		// reload): drop the stale working copy, it would overwrite the change
		if (this._subEditors.has(key) && this._subEditorSources.get(key) !== item.context) {
			this.dropSubEditor(key);
		}

		if (!this._subEditors.has(key)) {
			const editor = config.editorFactory({context: cloneContext(item.context)} as ReportContext);
			editor.grid = false;
			// commit directly on the subeditor's own edit events too - the
			// item visualization (the usual event source) may not be loaded
			editor.addEventListener('edit', () => this._commitSubEditor());
			this._subEditors.set(key, editor);
			this._subEditorCommits.set(key, JSON.stringify(item.context));
			this._subEditorSources.set(key, item.context);
		}

		const editor = this._subEditors.get(key);
		editor.visualization = this.itemVisualization;
		return editor;
	}

	private dropSubEditor(key: string) {
		this._subEditors.delete(key);
		this._subEditorCommits.delete(key);
		this._subEditorSources.delete(key);
	}

	/**
	 * Push the subeditor working copy of the selected item into the store
	 * (called from the edit events the subeditors dispatch on each change).
	 * Commits only when the working copy actually changed, so unrelated edit
	 * events (drag, resize) never overwrite the item with a stale copy.
	 */
	private _committing = false;
	private _commitSubEditor() {
		if (this._committing) return;
		const item = this.item;
		const editor = item?.uid ? this._subEditors.get(item.uid) : null;
		const working = editor?.reportContext?.context;
		if (!working) return;

		// late wiring: the item visualization may have finished loading after
		// the last editor render (live preview of the subeditor needs it)
		editor.visualization ??= this.itemVisualization;

		const json = JSON.stringify(working);
		if (this._subEditorCommits.get(item.uid) === json) return;
		this._subEditorCommits.set(item.uid, json);

		this._committing = true;
		try {
			this.visualization?.updateItem(this.itemId, it => {
				it.context = JSON.parse(json);
			});
			// our own commit produced a new item context snapshot - adopt it
			// as the working copy source so it does not count as external
			this._subEditorSources.set(item.uid, this.item?.context);
		} finally {
			this._committing = false;
		}
	}

	willUpdate() {
		this.grid = false;
	}

	/**
	 * Select the clicked/moved/resized item and update the geometry fields
	 * (a plain click in design mode also dispatches movestart/movestop).
	 */
	private _onItemGeometryChange = (e: DashboardEvent) => {
		if (e.model != null && e.model.index !== this.itemIndex) {
			this.itemIndex = e.model.index;
		} else {
			this.requestUpdate();
		}
	}

	/**
	 * Reflect item additions/removals (e.g. via the overlay remove button).
	 * The edit event fires on every change (including subeditor keystrokes),
	 * so only structural changes trigger a re-render. Subeditors are keyed
	 * by item uid, only the ones of removed items are dropped.
	 */
	private _itemCount = -1;
	private _onDashboardEdit = () => {
		const items = this.contextData?.items ?? [];
		if (items.length == this._itemCount) {
			// non-structural edit: sync the subeditor working copy into the store
			this._commitSubEditor();
			return;
		}

		this._itemCount = items.length;
		const uids = new Set(items.map((item: UidMultiContextDataItem) => item.uid));
		[...this._subEditors.keys()].forEach(uid => {
			if (!uids.has(uid)) this.dropSubEditor(uid);
		});
		if (this.itemIndex >= items.length) this.itemIndex = items.length - 1;
		this.requestUpdate();
	}

	/**
	 * Re-render when an item visualization loads - the subeditor of the
	 * selected item adopts it (and its report data) on the next render.
	 */
	private _onItemLoad = () => this.requestUpdate();

	private _addVisualizationListeners() {
		// registering the same listener twice is a no-op, safe to call repeatedly
		this.visualization?.addEventListener('dashboard.movestop', this._onItemGeometryChange);
		this.visualization?.addEventListener('dashboard.resizestop', this._onItemGeometryChange);
		this.visualization?.addEventListener('edit', this._onDashboardEdit);
		this.visualization?.addEventListener('report.load', this._onItemLoad);
	}

	private _removeVisualizationListeners(visualization = this.visualization) {
		visualization?.removeEventListener('dashboard.movestop', this._onItemGeometryChange);
		visualization?.removeEventListener('dashboard.resizestop', this._onItemGeometryChange);
		visualization?.removeEventListener('edit', this._onDashboardEdit);
		visualization?.removeEventListener('report.load', this._onItemLoad);
	}

	connectedCallback() {
		super.connectedCallback();
		// the editor instance is cached and re-attached when design mode is re-entered
		this._addVisualizationListeners();
		// restore the highlight of the previously selected item
		if (this.visualization && this.itemIndex > -1) {
			this.visualization.highlightItemId = this.itemId;
		}
	}

	updated(props: Map<PropertyKey, unknown>) {
		if (props.has('reportContext')) {
			this._subEditors.clear();
			this._subEditorCommits.clear();
			this._subEditorSources.clear();
		}

		if (props.has('visualization')) {
			this._removeVisualizationListeners(props.get('visualization') as DashboardVisualization);
			this._addVisualizationListeners();
		}

		const context = this.contextData;
		if (!context) return;
		this._itemCount = context.items?.length ?? 0;

		const items: TreeSource = [{k: '-1' , v: `<${msg('common properties')}>`}];
		context.items?.forEach((ctxItem, i) => {
			items.push({k: i.toString(), v: ctxItem.title || `item ${i + 1}`});
		});

		this.itemSelect.setSourceData(items, {
			selectMode: 'single',
			autoClose: true,
		});

		if (props.has('itemIndex') && this.visualization) {
			this.visualization.highlightItemId = this.itemId;
		}
	}

	_onItemChange = (e: TreeEvent) => {
		this.itemIndex = Number(e.node.key);
	}

	/**
	 * Write a dot-path value into the given record: 'unset' removes the own
	 * value (so it is inherited again), other values are set along the path.
	 */
	private writePath(record: Record<string, unknown>, parts: string[], value: unknown) {
		let subRecord = record;
		for (let i = 0; i < parts.length - 1; i++) {
			if (!subRecord[parts[i]]) subRecord[parts[i]] = {};
			subRecord = subRecord[parts[i]] as Record<string, unknown>;
		}

		if (value === 'unset') {
			delete subRecord[parts[parts.length - 1]];
		} else {
			subRecord[parts[parts.length - 1]] = value;
		}
	}

	_onInputChange = (e: events.ChangeEvent|TreeEvent) => {
		const { key, type } = (e.target as HTMLElement).dataset;
		const parts = key.split('.');

		let value: unknown = e instanceof TreeEvent ? (e.target as TreeDropdown).value : e.value;
		if (value !== 'unset') {
			switch (type) {
				case 'boolean':
					value = value === '1';
					break;
				case 'color':
					value = (value as string || '').replace(/^#+/g, '');
					break;
				case 'number':
					value = Number(value);
					break;
				case 'array':
					value = (value as [string][]).map(i => i[0]);
					break;
			}
		}

		if (!this.visualization) return;

		if (this.itemIndex > -1) {
			this.visualization.updateItem(this.itemId, item => {
				this.writePath(item as Record<string, unknown>, parts, value);
			});
			// geometry changes must be propagated into the dashboard model
			if (['x', 'y', 'width', 'height'].includes(parts[0])) {
				this.visualization.updateDashboard();
			}
		} else {
			// common properties live in view_options (style -> item_style)
			const optionParts = parts[0] == 'style' ? ['item_style', ...parts.slice(1)] : parts;
			this.visualization.updateViewOptions(viewOptions => {
				this.writePath(viewOptions, optionParts, value);
			});
		}

		// transparent toggles the visibility of the other style fields
		if (key == 'style.transparent') this.requestUpdate();
	}

	private numberList = (from: number, to: number) => Array.from(
		{length: to - from + 1},
		(_, i) => ({k: (from + i).toString(), v: (from + i).toString()})
	);

	renderDashboardPropertyForm = () => {
		// read from the live draft (this.contextData follows the store), the
		// store creates view_options on the first write
		const options: DashboardViewOptions = this.contextData?.view_options ?? {};

		const list100 = new Array(101).fill(0).map((_, i) => (i == 0 ? {k: '0', v: 'auto'} : {k: i.toString(), v: i.toString()}));
		const list500 = new Array(501).fill(0).map((_, i) => (i == 0 ? {k: '0', v: 'auto'} : {k: i.toString(), v: i.toString()}));

		return html`
			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="columns"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Columns')}"
					.items="${list100}"
					.value="${options.columns || constants.DASHBOARD_COLUMNS}">
				</og-dropdown-list>

				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="rows"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Rows')}"
					.items="${list100}"
					.value="${options.rows || 0}">
				</og-dropdown-list>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="columnWidth"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Column Width')}"
					.items="${list500}"
					.value="${options.columnWidth || 0}">
				</og-dropdown-list>

				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="rowHeight"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Row Height')}"
					.items="${list500}"
					.value="${options.rowHeight || 0}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="orientation"
					data-type="string"
					@change="${this._onInputChange}"
					.caption="${msg('Orientation')}"
					.items="${[{k: 'horizontal', v: msg('Horizontal')}, {k: 'vertical', v: msg('Vertical')}]}"
					.value="${options.orientation || 'vertical'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="gap"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Gap')}"
					.items="${list100}"
					.value="${options.gap || constants.DASHBOARD_ITEM_GAP}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="validate_access"
					data-type="boolean"
					@change="${this._onInputChange}"
					.caption="${msg('Validate access')}"
					.title="${msg('Hide texts and links to views the user cannot open')}"
					.items="${[{k: '1', v: msg('Yes')}, {k: '0', v: msg('No')}]}"
					.value="${options.validate_access === false ? '0' : '1'}">
				</og-dropdown-list>
			</div>
		`;
	};

	renderItemPropertyForm = () => html`
		<div class="row">
			<og-input
				class="field"
				data-key="title"
				data-type="string"
				@change="${this._onInputChange}"
				.placeholder="${msg('Title')}"
				.value="${this.item?.title || ''}">
			</og-input>
		</div>

		<div class="row" style="display: flex; flex-direction: row; gap: 4px">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="x"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('X')}"
				.items="${this.numberList(0, 99)}"
				.value="${(this.item?.x ?? 0).toString()}">
			</og-dropdown-list>

			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="y"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('Y')}"
				.items="${this.numberList(0, 99)}"
				.value="${(this.item?.y ?? 0).toString()}">
			</og-dropdown-list>
		</div>

		<div class="row" style="display: flex; flex-direction: row; gap: 4px">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="width"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('Width')}"
				.items="${this.numberList(1, 100)}"
				.value="${(this.item?.width ?? 8).toString()}">
			</og-dropdown-list>

			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="height"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('Height')}"
				.items="${this.numberList(1, 100)}"
				.value="${(this.item?.height ?? 4).toString()}">
			</og-dropdown-list>
		</div>
	`;

	renderStyleForm = () => html`
		<div class="row">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="style.transparent"
				data-type="boolean"
				@change="${this._onInputChange}"
				.caption="${msg('Transparent')}"
				.items="${[{k: '0', v: msg('No')}, {k: '1', v: msg('Yes'), icon: 'eye-slash'}]}"
				.value="${this.itemStyle?.transparent ? '1' : '0'}">
			</og-dropdown-list>
		</div>

		${this.itemStyle?.transparent ? '' : html`
		<div class="row">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="style.header"
				data-type="boolean"
				@change="${this._onInputChange}"
				.caption="${msg('Header')}"
				.items="${[{k: '1', v: msg('Visible'), icon: 'eye'}, {k: '0', v: msg('Hidden'), icon: 'eye-slash'}]}"
				.value="${(this.itemStyle?.header ?? true) ? '1' : '0'}">
			</og-dropdown-list>
		</div>

		<div class="row">
			<og-dropdown-colorpicker
				class="field"
				color="transparent"
				data-key="style.border.color"
				data-type="color"
				@change="${this._onInputChange}"
				.caption="${msg('Border color')}"
				.value="${this.itemStyle?.border?.color}">
			</og-dropdown-colorpicker>
		</div>

		<div class="row">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="style.border.width"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('Border width')}"
				.items="${[
					{k: 'unset', v: `<${msg('inherited')}>`},
					...new Array(11).fill(0).map((_, i) => ({k: i.toString(), v: i.toString()}))
				]}"
				.value="${(this.itemStyle?.border?.width ?? 'unset').toString()}">
			</og-dropdown-list>
		</div>

		<div class="row">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="style.border.style"
				data-type="string"
				@change="${this._onInputChange}"
				.caption="${msg('Border style')}"
				.items="${[
					{k: 'unset', v: `<${msg('inherited')}>`},
					...BORDER_STYLES.map(s => ({k: s, v: s}))
				]}"
				.value="${this.itemStyle?.border?.style ?? 'unset'}">
			</og-dropdown-list>
		</div>

		<div class="row">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="style.border.radius"
				data-type="number"
				@change="${this._onInputChange}"
				.caption="${msg('Border radius')}"
				.items="${[
					{k: 'unset', v: `<${msg('inherited')}>`},
					...new Array(101).fill(0).map((_, i) => ({k: i.toString(), v: i.toString()}))
				]}"
				.value="${(this.itemStyle?.border?.radius ?? 'unset').toString()}">
			</og-dropdown-list>
		</div>

		<div class="row">
			<og-dropdown-colorpicker
				class="field"
				color="transparent"
				data-key="style.color"
				data-type="color"
				@change="${this._onInputChange}"
				.caption="${msg('Title Color')}"
				.value="${this.itemStyle?.color}">
			</og-dropdown-colorpicker>
		</div>

		<div class="row">
			<og-dropdown-colorpicker
				class="field"
				color="transparent"
				data-key="style.fgColor"
				data-type="color"
				@change="${this._onInputChange}"
				.caption="${msg('Background')}"
				.value="${this.itemStyle?.fgColor}">
			</og-dropdown-colorpicker>
		</div>
		`}
	`;

	render = () => html`
		<div>
			<div class="row">
				<og-tree-dropdown
					color="transparent"
					.value="${[[this.itemIndex.toString()]]}"
					class="item-select"
					@change="${this._onItemChange}">
				</og-tree-dropdown>
			</div>
		</div>

		${this.itemIndex > -1 ? html`
			<div class="head">
				<og-bucket-button
					.text="${msg('Dashboard').toUpperCase()}"
					icon="cubes"
					@click="${() => this.activeTab = 'dashboard'}"
					?active="${this.activeTab == 'dashboard'}">
				</og-bucket-button>

				<og-bucket-button
					.text="${msg('Visualization').toUpperCase()}"
					icon="pen-ruler"
					@click="${() => this.activeTab = 'visualization'}"
					?active="${this.activeTab == 'visualization'}">
				</og-bucket-button>
			</div>	
		` : ''}

		<div class="body">
			${this.itemIndex > -1 && this.activeTab == 'visualization' ? html`
				<div class="container-subeditor">
					${this.subEditor ? this.subEditor : ''}
				</div>
			` : html`
				<og-container>
					<div slot="content">
						<og-panel
							collapsible
							?collapsed="${false}"
							@resizestop="${() => {}}">

							<div class="header" slot="header">
								<og-icon icon="input-text" fixed-width></og-icon> ${msg('Properties')}
							</div>

							<div class="body" slot="body" style="height: 100%; overflow: hidden">
								${this.itemIndex > -1 ? this.renderItemPropertyForm() : this.renderDashboardPropertyForm()}
							</div>
						</og-panel>

						<og-panel
							collapsible
							?collapsed="${false}"
							@resizestop="${() => {}}">

							<div class="header" slot="header">
								<og-icon icon="image" fixed-width></og-icon> ${msg('Style')}
							</div>

							<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderStyleForm()}</div>
						</og-panel>
					</div>
				</og-container>
			`}
		</div>
	`;

	disconnectedCallback() {
		super.disconnectedCallback();
		this._removeVisualizationListeners();
		// leaving design mode, clear the item highlight
		if (this.visualization) this.visualization.highlightItemId = null;
	}

	layout = () => {}

}
